import { useEffect, useMemo, useState } from 'react';
import { Icon, IconButton, LinearProgress, TablePagination, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Button, Typography, AlertColor } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { logService } from 'shared/services/grupoTeste/logService';
import { CanaisService } from 'shared/services/canais/canaisService';
import { IListagemLogsGrupo, listKeyValueTipoGrupoTeste } from 'shared/types';
import { FerramentasDaListagem, IListaFiltros, AlertaSnackBar } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { useSelectedOrganization } from 'shared/store/organization';
import { getValueFromKeyValue, IKeyValue, listagemCanalToIKeyValue, currentDateHour, stringToDateTime} from 'shared/utils';
import { useHasPermissionAcao } from 'shared/hooks';


export const ListagemDeLogsGrupoTeste: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [stateSnack, setStateSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const hasPermitionCreate = useHasPermissionAcao('grupoteste', 'CRE');
  const hasPermitionRead = useHasPermissionAcao('grupoteste', 'REA');

  const [rows, setRows] = useState<IListagemLogsGrupo[]>([]);

  const org = useSelectedOrganization();

  const [listKeyValueCanal, setListKeyValueCanal] = useState<IKeyValue[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [idCanal, setIdCanal] = useState('');

  const [isLoadingCanal, setIsLoadingCanal] = useState(true);

  const [eventoPesquisar, setEventoPesquisar] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const buscaNome = useMemo(() => {
    return searchParams.get('usu_id') || [];
  }, [searchParams]);

  const buscaIDGrupo = useMemo(() => {
    return searchParams.get('grp_id') || '';
  }, [searchParams]);

  const buscaNomeGrupo = useMemo(() => {
    return searchParams.get('grp_nm') || '';
  }, [searchParams]);

  const buscaDtInicio = useMemo(() => {
    return searchParams.get('dtInicio') || currentDateHour('primeira');
  }, [searchParams]);

  const buscaDtFim = useMemo(() => {
    return searchParams.get('dh_fim') || currentDateHour('ultima');
  }, [searchParams]);
  

  const offSet = useMemo(() => {
    return searchParams.get('offset') || '0';
  }, [searchParams]);

  const limit = useMemo(() => {
    return searchParams.get('limit') || '10';
  }, [searchParams]);

  const listaFiltros: IListaFiltros[] = [
    {
      tipo: 'TextFieldNumeric',
      mapValue: [],
      label: 'ID Usuario ',
      textoBusca: buscaNome,
      handleTextoBusca: texto => { searchParams.set('usu_id', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    
    {
      tipo: 'TextFieldNumeric',
      mapValue: [],
      label: 'ID Grupo',
      textoBusca: buscaIDGrupo,
      handleTextoBusca: texto => { searchParams.set('grp_id', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'TextField',
      mapValue: [],
      label: 'Nome Grupo',
      textoBusca: buscaNomeGrupo,
      handleTextoBusca: texto => { searchParams.set('grp_nm', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    
    {
      tipo: 'DateTimePicker',
      mapValue: [],
      label: t('global.dh_ini'),
      textoBusca: buscaDtInicio,
      handleTextoBusca: texto => { searchParams.set('dh_ini', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'DateTimePicker',
      mapValue: [],
      label: t('global.dh_fim'),
      textoBusca: buscaDtFim,
      handleTextoBusca: texto => { searchParams.set('dh_fim', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
  ];

  const handleLimpar = () => {
    // eslint-disable-next-line prefer-const
    let parametros: string[] = [];
    searchParams.forEach(function (value, key) {
      parametros.push(key);
    });
    parametros.map((item) => (
      searchParams.delete(item)
    ));
    setIdCanal('');
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handlePesquisar = () => {
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    searchParams.set('offset', (newPage * rowsPerPage).toString()); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    searchParams.set('limit', event.target.value.toString());
    searchParams.set('offset', '0');
    setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  

  const handleCloseSnack = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setStateSnack({ ...stateSnack, open: false });
  };

  useEffect(() => {
    setIsLoadingCanal(true);

    CanaisService.getAll()
      .then((result) => {
        setIsLoadingCanal(false);
        if (result instanceof Error) {
          setStateSnack({ open: true, severity: 'error', message: `Ocorreu o seguinte erro: ${result.message}` });
        } else {
          setListKeyValueCanal(listagemCanalToIKeyValue(result));
        }
      });
  }, []);

  useEffect(() => {
    setIsLoading(true);

    logService.getAll( Number(org?.CodigoOrganizacao),searchParams.toString())
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setStateSnack({ open: true, severity: 'error', message: `Ocorreu o seguinte erro: ${result.message}` });
        } else {
          setTotalCount(result.count);
          setRows(result.rows);
          setRowsPerPage(Number.parseInt(limit.toString()));
          setPage(Number.parseInt(offSet.toString()) / Number.parseInt(limit.toString()));
        }
      });

  }, [eventoPesquisar]);

  return (
    <>
      <LayoutBaseDePagina
        titulo={t('pages.grupoTeste.tituloLog')}

        drawerDeFiltros={
          <FerramentasDaListagem
            listaFiltros={listaFiltros}
            handlePesquisar={() => handlePesquisar()}
            handleLimpar={() => handleLimpar()}
          />
        }
      >
        <TableContainer component={Paper} variant="outlined" sx={{ m: 1, width: 'auto' }}>
          <Table stickyHeader >
            <TableHead>
              <TableRow>
                <TableCell> {t('pages.grupoTeste.usuarioNome')}</TableCell>
                <TableCell> {t('pages.grupoTeste.id')}</TableCell>       
                <TableCell> {t('pages.grupoTeste.nome')}</TableCell>            
                <TableCell> {t('pages.grupoTeste.tipo')}</TableCell>
                <TableCell> {t('pages.grupoTeste.canal')}</TableCell>
                <TableCell>{t('global.acoes')}</TableCell>
                <TableCell> {t('global.dataHora')}</TableCell>
              </TableRow>
            </TableHead>
            {!isLoading && (<TableBody>
              {rows.map((row) => (
                <TableRow key={row.NomeGrupoTeste}>
                  <TableCell>{row.UsuarioLog}</TableCell>
                  <TableCell>{row.IdGrupoTeste}</TableCell>
                  <TableCell>{row.NomeGrupoTeste}</TableCell>
                  <TableCell>{row.TipoGrupoTeste}</TableCell>
                  <TableCell>{row.IdComCanal}</TableCell>
                  <TableCell>{row.Acao}</TableCell>
                  <TableCell>{stringToDateTime(row.DataInclusao.toString())}</TableCell>
                 
                </TableRow>
              ))}
            </TableBody>)}

            {totalCount === 0 && !isLoading && (
              <caption>{t('global.listaVazia')}</caption>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <LinearProgress variant='indeterminate' />
                  </TableCell>
                </TableRow>
              )}
              {(!isLoading) && (
                <TableRow>
                  <TableCell colSpan={2}>
                    {hasPermitionCreate && (<Button
                      color='primary'
                      disableElevation
                      variant='outlined'
                      onClick={() => navigate('/grupoteste/listagem/')}
                      startIcon={<Icon>arrow_back</Icon>}
                    >
                      <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                        {t('components.actions.voltar')}
                      </Typography>
                    </Button>)}
                  </TableCell>
                  {(totalCount > 0 && totalCount > 10) && (<TableCell colSpan={8}>
                    <TablePagination
                      component="div"
                      labelDisplayedRows={({ from, to, count }) => `${from} - ${to} ${t('global.of')} ${count !== -1 ? count : `${t('global.moreThan')} ${to}`}`}
                      labelRowsPerPage={t('global.rowsPerPage')}
                      showFirstButton={true}
                      showLastButton={true}
                      page={page}
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[5, 8, 10, 20, 50, 100]}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    //onChange={(_, newPage) => setSearchParams({ busca, pagina: newPage.toString() }, { replace: true })}
                    />
                  </TableCell>
                  )}
                </TableRow>
              )}
            </TableFooter>
          </Table>
        </TableContainer>

      </LayoutBaseDePagina>
      <AlertaSnackBar
        open={stateSnack.open}
        severity={stateSnack.severity as AlertColor}
        message={stateSnack.message}
        handleClose={handleCloseSnack}
      />
    </>
  );
};